/**
 * Web Component wrapper for PaymentFormContainer.
 *
 * This module registers <donation-form> as a native Custom Element backed by
 * the full Nuxt payment form container (including dynamic template + thank-you loading).
 */

import { computed, defineCustomElement, getCurrentInstance, h, watch } from 'vue'
import { usePaymentForm } from '../shared/use-payment-form'
import {
  isSensePassWidgetQueryParam,
  normalizeSensePassWidgetFormIds,
  resolveClientRuntime,
  resolveCreditCardWidgetUrl,
  resolveSensePassWidgetVersionFromQuery,
} from '../shared/runtime-environment'
import type { SensePassWidgetFormIds } from '../shared/runtime-environment'
import type { Component } from 'vue'
import vue3DatepickerShadowCss from './vue3-datepicker-shadow.css?raw'
import {
  buildComponentRegistry,
  type ModuleWithComponent
} from './component-registry'
import { resolveFormComponentName } from './template-resolution'
import {
  resolveThankYouTemplateId,
  thankYouTemplateUsesMessage,
  toThankYouComponentName
} from '../shared/thank-you-template'

type PaymentFormElementProps = {
  id: string
  testMode: boolean
  subCampaign: string
  campaign: string
  callback: string
  checkText: string
  gateway: string
  noCharge: boolean
}

const DEBUG =
  typeof localStorage !== 'undefined' &&
  localStorage.getItem('donation-form-debug') === 'true'

const globalComponents: Record<string, Component> = {}

const baseComponentModules = import.meta.glob<ModuleWithComponent>('../components/**/*.vue', {
  eager: true
})

const templateComponentModules = import.meta.glob<ModuleWithComponent>(
  '../payment-form-templates/components/**/*.vue',
  { eager: true }
)

const thankYouTemplateModules = import.meta.glob<ModuleWithComponent>(
  '../thank-you-template/**/*.vue',
  { eager: true }
)

// Also load the form templates themselves (not just their components)
// Use **/*.vue to match all subdirectories
const paymentFormTemplates = import.meta.glob<ModuleWithComponent>(
  '../payment-form-templates/**/*.vue',
  { eager: true }
)

const pledgeFormTemplates = import.meta.glob<ModuleWithComponent>(
  '../pledge-form-templates/**/*.vue',
  { eager: true }
)

if (DEBUG) {
  console.log('[Loading] Base components:', Object.keys(baseComponentModules).length)
  console.log('[Loading] Template components:', Object.keys(templateComponentModules).length)
  console.log('[Loading] Thank you templates:', Object.keys(thankYouTemplateModules).length)
  console.log('[Loading] Payment form templates:', Object.keys(paymentFormTemplates).length)
  console.log('[Loading] Pledge form templates:', Object.keys(pledgeFormTemplates).length)

  // Show sample paths
  console.log('[Loading] Sample payment form templates:', Object.keys(paymentFormTemplates).slice(0, 5))
}

Object.assign(globalComponents, buildComponentRegistry({
  baseComponentModules,
  templateComponentModules,
  thankYouTemplateModules,
  paymentFormTemplates,
  pledgeFormTemplates
}))

if (DEBUG) {
  console.log('[Loading] Total registered components:', Object.keys(globalComponents).length)
  console.log('[Loading] Sample registered names:', Object.keys(globalComponents).filter(k => k.includes('ncsy') || k.includes('Ncsy')))
}

const webComponentScriptSrc = typeof document !== 'undefined'
  ? document.currentScript?.src
  : undefined

type DonationFormRuntimeConfig = {
  sensepassWidgetFormIds?: unknown
}

function resolveWebComponentSensePassWidgetFormIds(): SensePassWidgetFormIds {
  if (typeof window === 'undefined') return undefined

  const hostWindow = window as Window & {
    __OU_DONATION_FORM_CONFIG__?: DonationFormRuntimeConfig
    __NUXT__?: {
      config?: {
        public?: DonationFormRuntimeConfig
      }
    }
  }
  const configuredFormIds =
    hostWindow.__OU_DONATION_FORM_CONFIG__?.sensepassWidgetFormIds ??
    hostWindow.__NUXT__?.config?.public?.sensepassWidgetFormIds

  return normalizeSensePassWidgetFormIds(configuredFormIds)
}

function log(...args: unknown[]) {
  if (DEBUG) {
  console.log('[Credit Card]', ...args)
  }
}

function ensureRuntimeConfig(apiUrl: string) {
  if (typeof window === 'undefined') return

  const nuxt = ((window as any).__NUXT__ = (window as any).__NUXT__ || {})
  nuxt.config = nuxt.config || { public: {} }
  nuxt.config.public = nuxt.config.public || {}
  nuxt.config.public.apiUrl = apiUrl

  ;(window as any).__PAYMENT_FORM_API_URL__ = apiUrl
}

// Create custom element using inline component definition (not configureApp)
// This approach works better with Vue 3 custom elements
const rawElement = defineCustomElement<{ [K in keyof PaymentFormElementProps]: PaymentFormElementProps[K] }>({
  name: 'donation-form',
  styles: [vue3DatepickerShadowCss],
  props: {
    id: { type: String, default: '' },
    testMode: { type: Boolean, default: false },
    subCampaign: { type: String, default: '' },
    campaign: { type: String, default: '' },
    callback: { type: String, default: '' },
    checkText: { type: String, default: '' },
    gateway: { type: String, default: '' },
    noCharge: { type: Boolean, default: false }
  },
  setup(props) {
    // Register global components directly on the current instance
    const instance = getCurrentInstance()
    if (instance) {
      log('Registering global components on instance')
      Object.assign(instance.appContext.components, globalComponents)
    }

    const runtime = computed(() => resolveClientRuntime({
      locationOrigin: typeof window !== 'undefined' ? window.location.origin : undefined,
      scriptSrc: webComponentScriptSrc,
      explicitTestMode: props.testMode
    }))

    const resolvedApiUrl = computed(() => runtime.value.apiUrl)
    const testMode = computed(() => runtime.value.testMode)

    watch(
      resolvedApiUrl,
      value => {
        log('Updating runtime config apiUrl to', value)
        ensureRuntimeConfig(value)
      },
      { immediate: true }
    )

    log('Mounting payment form with props:', props)

    // Emit a CustomEvent on every successful donation from the machine's success entry action.
    // Sensitive payment fields are stripped to prevent PCI data leaking to the host page.
    let emitOnSuccess: (() => void) | null = null

    // Use payment form context and logic directly here
    // instead of nesting another component
    const paymentForm = usePaymentForm(props.id, {
      testMode: testMode.value,
      noCharge: props.noCharge,
      onSuccess: () => emitOnSuccess?.()
    })

    if (!paymentForm) {
      log('ERROR: PaymentForm context not available')
      return () => h('div', {
        style: {
          padding: '20px',
          background: '#ffebee',
          border: '1px solid #f44336',
          borderRadius: '4px'
        }
      }, [
        h('h3', {}, 'Error'),
        h('p', {}, 'Payment form context not available')
      ])
    }

    const { submitted, settings, request } = paymentForm
    const availableThankYouTemplates = Object.keys(thankYouTemplateModules)

    emitOnSuccess = () => {
      if (!request.value) return
      const el = instance?.proxy?.$el
      const host = el?.getRootNode()?.host
      if (host) {
        const { CardNumber, SecurityCode, achToken, routingNumber, expirationMonth, expirationYear, token, taxId, ...safeData } = request.value as any
        log('donation-submitted: host=', host, 'safeData=', safeData)
        host.dispatchEvent(new CustomEvent('donation-submitted', {
          bubbles: true,
          composed: true,
          detail: safeData
        }))
      }
    }

    // useRoute() doesn't work in custom elements, create a simple route object from window.location
    const route = {
      query: (() => {
        if (typeof window === 'undefined') return {}
        const params = new URLSearchParams(window.location.search)
        const query: Record<string, string> = {}
        for (const [key, value] of params.entries()) {
          query[key] = value
        }
        return query
      })()
    }
    const sensepassWidgetVersion = resolveSensePassWidgetVersionFromQuery(route.query)

    log('Payment form initialized', { settings: settings, route: route })

    // Apply URL params to the form once the machine is ready
    const { setValue, actorRef } = paymentForm
    if (actorRef && Object.keys(route.query).length > 0) {
      let hasApplied = false
      const subscription = actorRef.subscribe((snapshot: any) => {
        if (snapshot.value === 'editing' && !hasApplied) {
          hasApplied = true
          for (const [key, value] of Object.entries(route.query)) {
            if (isSensePassWidgetQueryParam(key)) continue

            if (value !== undefined && value !== null && value !== '') {
              log('Applying URL param:', key, '=', value)
              // Currency options are uppercase (USD, ILS, CAD) — normalize
              setValue(key as any, key === 'currency' ? value.toUpperCase() : value)
            }
          }
          subscription.unsubscribe()
        }
      })
    }

    // Load credit card widget script
    // Use onMounted equivalent for custom elements
    if (typeof window !== 'undefined' && typeof document !== 'undefined') {
      const sensepassWidgetFormIds = resolveWebComponentSensePassWidgetFormIds()
      // Check if script is already loaded
      const existingScript = document.querySelector('script[src*="credit-card"]')
      if (!existingScript) {
        log('Loading credit card widget script')
        const script = document.createElement('script')
        script.defer = true
        script.type = 'module'
        script.src = resolveCreditCardWidgetUrl(
          testMode.value,
          props.id,
          sensepassWidgetFormIds,
          sensepassWidgetVersion
        )
        document.body.appendChild(script)
        log('Credit card widget script added to document')
      } else {
        log('Credit card widget script already loaded')
      }
    }

    // Return render function with inline logic
    return () => {
      try {
        log('Render function start')
        log('props.id:', props.id)
        log('submitted type:', typeof submitted, 'value:', submitted)
        log('settings type:', typeof settings, 'value:', settings)
        log('route type:', typeof route, 'value:', route)

        log('Accessing submitted.value...')
        const submittedValue = submitted.value
        log('submitted.value =', submittedValue)

        log('Render function executing', {
          id: props.id,
          submitted: submittedValue,
          hasSettings: !!settings
        })

        // Check if submitted
        log('Accessing route.query...')
        const routeQuery = route.query
        log('route.query =', routeQuery)

        const isSubmitted = routeQuery.thankyou !== undefined || submittedValue
        log('isSubmitted check:', isSubmitted, 'query:', routeQuery, 'submitted:', submittedValue)

        if (isSubmitted) {
          const thankYouTemplateId = resolveThankYouTemplateId(
            props.id,
            settings,
            availableThankYouTemplates
          )
          const thankYouComponentName = toThankYouComponentName(thankYouTemplateId)
          const thankYouComponent = instance?.appContext.components[thankYouComponentName]

          log('Rendering thank you page with component:', {
            thankYouTemplateId,
            thankYouComponentName,
            found: !!thankYouComponent
          })

          if (thankYouComponent) {
            return h(
              thankYouComponent,
              thankYouTemplateUsesMessage(thankYouTemplateId)
                ? { message: settings?.message }
                : {}
            )
          }

          log('Thank you component not found, falling back to generic template')
          const fallbackComponent = instance?.appContext.components['generic-thank-you']

          if (fallbackComponent) {
            return h(fallbackComponent, { message: settings?.message })
          }

          return h('div', { class: 'thank-you-container' }, [
            h('p', {}, settings?.message || 'Thank you for your donation.')
          ])
        }

        // Try to render the form template
        const formComponentName = resolveFormComponentName(props.id, settings)
        log('Attempting to render form, checking for component:', formComponentName)
        log('Has instance?', !!instance)
        log('Has appContext?', !!instance?.appContext)
        log('Has components?', !!instance?.appContext?.components)
        log('Total components registered:', instance ? Object.keys(instance.appContext.components).length : 0)
        log('Looking for component:', formComponentName)

        if (instance && instance.appContext.components[formComponentName]) {
          const formComponent = instance.appContext.components[formComponentName]
          log('Found component in registry! Type:', typeof formComponent, 'Name:', formComponent?.name || formComponent?.__name)

          const result = h('div', { class: 'payment-form-container' }, [
            h(formComponent, {
              subCampaign: props.subCampaign,
              campaign: props.campaign,
              checkText: props.checkText
            })
          ])

          log('Created vnode for form:', result)
          return result
        } else {
          const allComponents = instance ? Object.keys(instance.appContext.components) : []
          log('Component NOT found in registry. Total:', allComponents.length)
          log('First 20:', allComponents.slice(0, 20))
          log('Looking for any with "ncsy":', allComponents.filter(c => c.toLowerCase().includes('ncsy')).slice(0, 10))
          log('Looking for any with "ignite":', allComponents.filter(c => c.toLowerCase().includes('ignite')).slice(0, 10))
        }

        // Fallback
        log('Rendering fallback message')
        return h('div', {
          style: {
            padding: '20px',
            background: '#fff3cd',
            border: '1px solid #ffc107',
            borderRadius: '4px'
          }
        }, [
          h('h3', {}, 'Form Loading...'),
          h('p', {}, `Form ID: ${props.id}`),
          h('p', { style: { fontSize: '12px', color: '#666' } }, `Available components: ${instance ? Object.keys(instance.appContext.components).length : 0}`)
        ])
      } catch (error) {
        log('ERROR in render function:', error)
        return h('div', {
          style: {
            padding: '20px',
            background: '#ffebee',
            border: '1px solid #f44336',
            borderRadius: '4px'
          }
        }, [
          h('h3', {}, 'Error'),
          h('p', {}, String(error))
        ])
      }
    }
  }
})

const OUPaymentFormElement = rawElement as unknown as CustomElementConstructor

if (!customElements.get('donation-form')) {
  customElements.define('donation-form', OUPaymentFormElement)
}

export { OUPaymentFormElement }
